import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { useMaps } from "../components/MapsProvider";
import { Line, Pie } from "react-chartjs-2";
import RefreshButton from "../components/admin/RefreshButton";
import { SECONDARY_API_URL } from "../config/api";
import {
  Chart as ChartJS,
  LineElement,
  PointElement,
  CategoryScale,
  LinearScale,
  Tooltip,
  Legend,
  ArcElement,
  TimeScale,
  Title,
  BarElement,
} from "chart.js";

// Register the required Chart.js components
ChartJS.register(
  LineElement,
  PointElement,
  CategoryScale,
  LinearScale,
  Tooltip,
  Legend,
  ArcElement,
  TimeScale,
  Title,
  BarElement
);

// Status color mapping for charts with white for pending
const chartStatusColors = {
  pending: "#ffffff", // white
  confirmed: "#4ade80", // green
  completed: "#60a5fa", // blue
  cancelled: "#ef4444", // red
};

const statusColors = {
  confirmed: "#22c55e", // green
  pending: "#ffffff", // white
  cancelled: "#ef4444", // red
  completed: "#3b82f6" // blue
};

const statusBadgeColors = {
  confirmed: "bg-green-600 text-white",
  pending: "bg-white text-black",
  cancelled: "bg-red-600 text-white",
  completed: "bg-blue-600 text-white"
};

const DashboardOverview = () => {
  const [stats, setStats] = useState({ total: 0, thisMonth: 0, earnings: 0, completionRate: 0 });
  const [bookings, setBookings] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const { isLoaded, forceLoaded } = useMaps();
  
  // Log the map loading status to help with debugging
  useEffect(() => {
    console.log("Maps loading status in DashboardOverview:", { isLoaded, forceLoaded });
  }, [isLoaded, forceLoaded]);
  
  const fetchStats = useCallback(async () => {
    setIsLoading(true);
    try {
      const res = await axios.get(`${SECONDARY_API_URL}/api/stats`);
      setStats({
        total: res.data.totalBookings,
        thisMonth: res.data.bookingsThisMonth,
        earnings: parseFloat(res.data.estimatedEarnings),
        completionRate: parseInt(res.data.completionRate, 10),
      });
      console.log("Stats fetched successfully");
    } catch (err) {
      console.error("Failed to fetch stats", err);
    } finally {
      setIsLoading(false);
    }
  }, []);
  
  const fetchRecentBookings = useCallback(async () => {
    try {
      const res = await axios.get(`${SECONDARY_API_URL}/api/bookings?limit=5`);
      if (res.data && Array.isArray(res.data)) {
        setBookings(res.data);
      }
    } catch (err) {
      console.error("Failed to fetch recent bookings", err);
    }
  }, []);
  
  useEffect(() => {
    fetchStats();
    fetchRecentBookings();
  }, [fetchStats, fetchRecentBookings]);
  
  const prepareEarningsTrendData = () => {
    const months = [...Array(6)].map((_, i) => {
      const date = new Date();
      date.setMonth(date.getMonth() - (5 - i));
      return date.toLocaleString("default", { month: "short", year: "numeric" });
    });

    const data = months.map(label => {
      const [month, year] = label.split(" ");
      return bookings
        .filter(b => {
          const d = new Date(b.datetime);
          return d.toLocaleString("default", { month: "short" }) === month && d.getFullYear().toString() === year;
        })
        .reduce((sum, b) => sum + parseFloat(b.price || 0), 0)
        .toFixed(2);
    });

    return {
      labels: months,
      datasets: [{
        label: "Monthly Earnings (€)",
        data,
        borderColor: "#ffffff",
        backgroundColor: "rgba(255, 255, 255, 0.1)",
        tension: 0.3
      }]
    };
  };

  const prepareStatusDistributionData = () => {
    const counts = bookings.reduce((acc, b) => {
      const status = b.status || "pending";
      acc[status] = (acc[status] || 0) + 1;
      return acc;
    }, {});

    // Ensure all status types are represented
    Object.keys(statusColors).forEach(status => {
      if (!counts[status]) counts[status] = 0;
    });

    const labels = Object.keys(counts).map(s => s.charAt(0).toUpperCase() + s.slice(1));
    const data = Object.values(counts);
    const backgroundColor = Object.keys(counts).map(status => chartStatusColors[status] || "#888888");

    return {
      labels,
      datasets: [{
        data,
        backgroundColor,
        borderWidth: 1
      }]
    };
  };
  
  const formatPrice = (value) => new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "EUR"
  }).format(value);
  
  const chartOptions = {
    responsive: true,
    maintainAspectRatio: true,
    aspectRatio: 2,
    plugins: {
      legend: {
        position: 'bottom',
        labels: {
          boxWidth: 12,
          font: {
            size: 10
          },
          color: '#f3f4f6' // Light gray color for better readability
        }
      },
      tooltip: {
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        titleColor: '#ffffff',
        bodyColor: '#ffffff',
        displayColors: true,
        borderWidth: 1,
        borderColor: '#ffffff' // White border for tooltips
      }
    }
  };
  
  return (
    <div>
      <div className="mb-6 flex flex-col md:flex-row justify-between items-start md:items-center">
        <div>
          <h1 className="text-3xl font-bold text-white mb-2">Dashboard Overview</h1>
          <p className="text-gray-400">Welcome to your <span style={{ fontFamily: 'Playfair Display, serif' }}>Lasmari Chauffeurs</span> management dashboard</p>
        </div>
        <RefreshButton 
          onClick={() => {
            fetchStats();
            fetchRecentBookings();
          }}
          isLoading={isLoading}
        />
      </div>
      
      {/* Stats */}
      <div className="grid md:grid-cols-4 gap-4 mb-6">
        <div className="bg-[#121212] rounded-xl shadow-lg p-4 border border-white/20 hover:border-white/40 transition-colors">
          <p className="text-white mb-1">Total Bookings</p>
          <h2 className="text-3xl font-bold text-white">{stats.total}</h2>
        </div>
        <div className="bg-[#121212] rounded-xl shadow-lg p-4 border border-white/20 hover:border-white/40 transition-colors">
          <p className="text-white mb-1">This Month</p>
          <h2 className="text-3xl font-bold text-white">{stats.thisMonth}</h2>
        </div>
        <div className="bg-[#121212] rounded-xl shadow-lg p-4 border border-white/20 hover:border-white/40 transition-colors">
          <p className="text-white mb-1">Earnings</p>
          <h2 className="text-3xl font-bold text-white">{formatPrice(stats.earnings)}</h2>
        </div>
        <div className="bg-[#121212] rounded-xl shadow-lg p-4 border border-white/20 hover:border-white/40 transition-colors">
          <p className="text-white mb-1">Completion Rate</p>
          <h2 className="text-3xl font-bold text-white">{stats.completionRate}%</h2>
        </div>
      </div>
      
      {/* Charts */}
      <div className="grid md:grid-cols-2 gap-6 mb-6">
        <div className="bg-[#121212] rounded-xl shadow-lg p-4 border border-white/20">
          <h3 className="text-lg font-bold text-white mb-3">Earnings Trend</h3>
          <div className="h-64">
            <Line data={prepareEarningsTrendData()} options={chartOptions} />
          </div>
        </div>
        <div className="bg-[#121212] rounded-xl shadow-lg p-4 border border-white/20">
          <h3 className="text-lg font-bold text-white mb-3">Booking Status</h3>
          <div className="h-64">
            <Pie data={prepareStatusDistributionData()} options={chartOptions} />
          </div>
        </div>
      </div>
      
      {/* Quick Links */}
      <div className="grid md:grid-cols-3 gap-4 mb-6">
        <Link 
          to="/admin-dashboard/bookings" 
          className="bg-gradient-to-br from-white to-gray-200 rounded-xl shadow-lg p-4 text-black hover:from-gray-100 hover:to-white transition-colors"
        >
          <h3 className="font-bold text-lg mb-1">Manage Bookings</h3>
          <p>View and manage all customer bookings</p>
        </Link>
        <Link 
          to="/admin-dashboard/analytics" 
          className="bg-[#121212] rounded-xl shadow-lg p-4 border border-white/30 hover:border-white transition-colors"
        >
          <h3 className="font-bold text-lg text-white mb-1">View Analytics</h3>
          <p className="text-gray-300">Explore detailed performance metrics</p>
        </Link>
        <Link 
          to="/admin-dashboard/settings" 
          className="bg-[#121212] rounded-xl shadow-lg p-4 border border-white/30 hover:border-white transition-colors"
        >
          <h3 className="font-bold text-lg text-white mb-1">Settings</h3>
          <p className="text-gray-300">Configure your business preferences</p>
        </Link>
      </div>
      
      {/* Recent Bookings */}
      <div className="bg-[#121212] rounded-xl shadow-lg p-4 border border-white/20">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-bold text-white">Recent Bookings</h3>
          <Link to="/admin-dashboard/bookings" className="text-white hover:text-gray-300">
            View All →
          </Link>
        </div>
        
        {isLoading ? (
          <div className="flex justify-center items-center h-32">
            <div className="w-8 h-8 border-4 border-white border-t-transparent rounded-full animate-spin"></div>
          </div>
        ) : bookings.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-white/20">
                  <th className="text-left py-2 text-white">Customer</th>
                  <th className="text-left py-2 text-white">Route</th>
                  <th className="text-left py-2 text-white">Date</th>
                  <th className="text-left py-2 text-white">Status</th>
                </tr>
              </thead>
              <tbody>
                {bookings.map(booking => (
                  <tr key={booking.id} className="border-b border-gray-800 hover:bg-black/30">
                    <td className="py-2">{booking.full_name}</td>
                    <td className="py-2 text-sm">{booking.pickup_name} → {booking.dropoff_name}</td>
                    <td className="py-2 text-sm">{new Date(booking.datetime).toLocaleDateString()}</td>
                    <td className="py-2">
                      <span className={`inline-block px-2 py-1 rounded-full text-xs ${statusBadgeColors[booking.status]}`}>
                        {booking.status.charAt(0).toUpperCase() + booking.status.slice(1)}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-center py-8 text-gray-400">
            No recent bookings found
          </div>
        )}
      </div>
    </div>
  );
};

export default DashboardOverview; 